import type { GmRewardType } from './utils/types';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Button, buttonVariants } from '@/components/ui/Button/button';
import { Link } from '@/i18n/routing';
import { cn } from '@/utils/cn';
import { GmBadge } from '../GmBadge';
import { GmLockedBadge } from '../GmLockedBadge';
import { GmRewardGiftBadge } from '../GmRewardGiftBadge';

interface Props {
  reward: GmRewardType;
  onClick?: VoidFunction;
  disabled?: boolean;
  className?: string;
}

export const GmAwardCard = ({
  onClick,
  disabled,
  reward,
  className,
}: Props) => {
  const {
    title,
    href,
    buttonLabelKey,
    lockBadge,
    subTitle,
    availableAmount,
    rewardNotificationBadge = 'count',
    rewardImg,
  } = reward;
  const t = useTranslations('gamification');

  // Boolean() keeps an amount of 0 from being rendered as a stray "0".
  const showBadge = Boolean(lockBadge) || Boolean(availableAmount);
  const isGiftBadge = rewardNotificationBadge === 'gift' && Boolean(availableAmount);

  const actionText = t(buttonLabelKey ?? 'open');
  const isActionDisabled = disabled || (!href && !onClick);
  const actionClassName = buttonVariants({ variant: 'primary', size: 'md', className: 'h-8 px-4' });

  const rewardTitle = t(`perks.titles.${title}`);

  const renderBadge = () => {
    if (lockBadge?.kind === 'coming-soon') {
      return <GmLockedBadge badge={lockBadge} className="py-[0.438rem]" />;
    }

    if (isGiftBadge) {
      return <GmRewardGiftBadge />;
    }

    const badgeLabel = lockBadge?.kind === 'needs-status'
      ? t('need-status', { status: lockBadge.statusName.toUpperCase() })
      : availableAmount;

    return (
      <GmBadge variant="desktop" className="min-w-7 py-[0.438rem] border-0 gm-gold-gradient">
        {badgeLabel}
      </GmBadge>
    );
  };

  return (
    <div className={cn('rounded-xl relative glass-stroke-border shadow-card gm-award-card-gradient px-4 py-3', className)}>
      {
        showBadge && (
          <div className="absolute bg-[#602544] rounded-full top-1.5 z-1 right-1.5">
            {renderBadge()}
          </div>
        )
      }
      <div className="flex max-w-37.5 md:max-w-40 h-full justify-between flex-col items-start">
        <div className={cn('flex flex-col gap-1', disabled && 'opacity-40')}>
          <p className="font-normal max-w-[9.063rem] text-gradient bg-promo-banner-title-gradient w-fit text-xl leading-5">
            {rewardTitle}
          </p>
          {
            subTitle && (
              <p className="text-base font-medium leading-6">
                {subTitle}
              </p>
            )
          }
        </div>
        {
          // disabled must block navigation too, so fall back to a disabled Button instead of a Link
          href && !disabled
            ? (
                <Link href={href} className={actionClassName}>
                  {actionText}
                </Link>
              )
            : (
                <Button
                  type="button"
                  variant="primary"
                  onClick={onClick}
                  disabled={isActionDisabled}
                  className={cn(actionClassName, 'relative')}
                >
                  {actionText}
                </Button>
              )
        }
      </div>
      <div className={cn('right-0 top-0 h-35 w-42.5 absolute', disabled && 'opacity-40')}>
        <Image
          fill
          alt={rewardTitle}
          src={rewardImg}
        />
      </div>
    </div>
  );
};
